import {Component, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {MatTableDataSource} from '@angular/material/table';
import {TeamCompanyUsersService} from '../../services/teamcompanyusers/teamcompanyusers.service';
import {MatSort} from '@angular/material/sort';
import {MatSnackBar} from '@angular/material/snack-bar';
import {UiAlertService} from '../../services/ui-alert/ui-alert.service';
import {SnackBarComponent} from '../snackbar/snackbar.component';
import {MatDialog} from '@angular/material/dialog';
import {SetTeamCompanyComponentModel, SetTeamCompanyDialogComponent} from '../modal/setteamcompanydialog/setteamcompanydialog.component';
import {
  EditCompanyManagersDialogComponent,
  EditCompanyManagersDialogModel,
} from '../modal/editcompanymanagersdialog/editcompanymanagersdialog.component';
import {TeamCompaniesService} from '../../services/teamcompanies/teamcompanies.service';

interface TeamCompanyUser {
  id: string;
  companyId?: string;
  companyName: string;
  role: string;
  createdAt: string;
  modifiedAt?: string;
  name: string;
  email?: string;
  userId?: string;
  grantorUserId?: string;
  metadata?: any;
  grantorName?: string;
  grantorEmail?: string;
}

interface GranteePermissionGroup {
  granteeKey: string;
  granteeUserId: string;
  granteeName: string;
  granteeEmail: string;
  permissions: TeamCompanyUser[];
  permissionCount: number;
  companyNames: string;
  roles: string;
  grantorNames: string;
  latestCreatedAt: string;
  filterText: string;
}

type PermissionDisplayMode = 'rows' | 'grantee';

@Component({
    selector: 'app-products-permissions',
    templateUrl: './products-permissions.component.html',
    styleUrls: ['./products-permissions.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ProductsPermissionsComponent implements OnInit {

  @ViewChild('permissionSort') set permissionSort(sort: MatSort) {
    if (sort) {
      this.permissions.sort = sort;
    }
  }

  @ViewChild('granteeSort') set granteeSort(sort: MatSort) {
    if (sort) {
      this.granteePermissions.sort = sort;
    }
  }

  public permissions = new MatTableDataSource<TeamCompanyUser>();
  public granteePermissions = new MatTableDataSource<GranteePermissionGroup>();

  public columnsToDisplay = ['companyName', 'role', 'createdAt', 'name', 'grantorName', 'actionmenu'];
  public granteeColumnsToDisplay = ['granteeName', 'permissionCount', 'permissions', 'latestCreatedAt', 'actionmenu'];
  public filter = '';
  public displayMode: PermissionDisplayMode = 'rows';
  public loading = false;
  public readonly roleOptions = [
    {value: 'manager', label: 'Team Manager'},
    {value: 'teamagent', label: 'Team Agent'},
    {value: 'supportmanager', label: 'Support Manager'},
    {value: 'supportagent', label: 'Support Agent'},
    {value: 'broker', label: 'Broker'},
    {value: 'agency', label: 'Agency'},
    {value: 'member', label: 'Agency Member'},
    {value: 'client', label: 'Client'},
    {value: 'payrolladmin', label: 'Payroll Admin'},
  ];

  constructor(
      private teamCompanyUsersService: TeamCompanyUsersService,
      private teamCompaniesService: TeamCompaniesService,
      private snackBar: MatSnackBar,
      private uiAlertService: UiAlertService,
      private dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    this.permissions.filterPredicate = (permission, filter) => this.getPermissionFilterText(permission).includes(filter);
    this.granteePermissions.filterPredicate = (group, filter) => group.filterText.includes(filter);
    this.loadPermissions();
  }

  applyFilter(): void {
    const normalizedFilter = this.normalizeSearchText(this.filter);
    this.permissions.filter = normalizedFilter;
    this.granteePermissions.filter = normalizedFilter;
  }

  clearFilter(): void {
    this.filter = '';
    this.applyFilter();
  }

  setDisplayMode(displayMode: PermissionDisplayMode): void {
    this.displayMode = displayMode;
    this.applyFilter();
  }

  trackByPermissionId(_index: number, permission: TeamCompanyUser): string {
    return permission.id;
  }

  getRoleLabel(role: string): string {
    return this.roleOptions.find(roleOption => roleOption.value === role)?.label || role;
  }

  getGrantorName(permission: TeamCompanyUser): string {
    return permission?.grantorName || '';
  }

  getGrantorEmail(permission: TeamCompanyUser): string {
    return permission?.grantorEmail || '';
  }

  async updatePermissionRole(permission: TeamCompanyUser, role: string): Promise<void> {
    if (!permission?.id || permission.role === role) {
      return;
    }

    try {
      await this.teamCompanyUsersService.modifyCompanyUser(permission.id, {role});
      permission.role = role;
      this.refreshPermissionData([...this.permissions.data]);
      this.snackBar.openFromComponent(SnackBarComponent, {data: 'Permission role updated'});
    } catch (error) {
      this.snackBar.openFromComponent(SnackBarComponent, {data: 'Unable to update permission role'});
    }
  }

  async removePermission(permission: TeamCompanyUser): Promise<void> {
    if (!permission?.id) {
      return;
    }

    const ok = await this.uiAlertService.presentAlertConfirm(
      `Do you really want to remove ${permission.name}'s ${permission.role} permission for ${permission.companyName}?`
    );
    if (!ok) {
      return;
    }

    try {
      await this.teamCompanyUsersService.deleteCompanyUser(permission.id);
      this.refreshPermissionData(this.permissions.data.filter(item => item.id !== permission.id));
      this.snackBar.openFromComponent(SnackBarComponent, {data: 'Permission removed'});
    } catch (error) {
      this.snackBar.openFromComponent(SnackBarComponent, {data: 'Unable to remove permission'});
    }
  }

  async removeGrantee(group: GranteePermissionGroup): Promise<void> {
    if (!group?.permissions?.length) {
      return;
    }

    const ok = await this.uiAlertService.presentAlertConfirm(
      `Do you really want to remove all permissions for ${group.granteeName}?`
    );
    if (!ok) {
      return;
    }

    try {
      await Promise.all(group.permissions.map(permission => this.teamCompanyUsersService.deleteCompanyUser(permission.id)));
      const removedPermissionIds = new Set(group.permissions.map(permission => permission.id));
      this.refreshPermissionData(this.permissions.data.filter(permission => !removedPermissionIds.has(permission.id)));
      this.snackBar.openFromComponent(SnackBarComponent, {data: 'Grantee permissions removed'});
    } catch (error) {
      this.snackBar.openFromComponent(SnackBarComponent, {data: 'Unable to remove grantee permissions'});
    }
  }

  addPermissionToGrantee(group: GranteePermissionGroup): void {
    this.addGrantee();
  }

  addGrantee(): void {
    const companyDialogRef = this.dialog.open(SetTeamCompanyDialogComponent, {
      data: new SetTeamCompanyComponentModel(
        '',
        'Select Company',
        'Choose the company where the grantee permission should be added.',
        'Continue',
        'Cancel',
        false
      )
    });
    companyDialogRef.afterClosed().subscribe(companyId => {
      if (!companyId) {
        return;
      }
      this.teamCompaniesService.getCompany(companyId).then(company => {
        this.openCompanyRolesDialog(company);
      }).catch(() => {
        this.snackBar.openFromComponent(SnackBarComponent, {data: 'Unable to load selected company'});
      });
    });
  }

  private openCompanyRolesDialog(company: any): void {
    const dialogRef = this.dialog.open(EditCompanyManagersDialogComponent, {
      data: new EditCompanyManagersDialogModel(company)
    });
    dialogRef.afterClosed().subscribe(() => {
      this.loadPermissions();
    });
  }

  private loadPermissions(): void {
    this.loading = true;
    this.teamCompanyUsersService.getAllCompanyUsers().then(result => {
      this.refreshPermissionData(result || []);
    }).finally(() => {
      this.loading = false;
    });
  }

  private refreshPermissionData(permissions: TeamCompanyUser[]): void {
    this.permissions.data = permissions;
    this.granteePermissions.data = this.buildGranteePermissionGroups(permissions);
    this.applyFilter();
  }

  private buildGranteePermissionGroups(permissions: TeamCompanyUser[]): GranteePermissionGroup[] {
    const groups = new Map<string, TeamCompanyUser[]>();

    permissions.forEach(permission => {
      const granteeKey = this.getGranteeKey(permission);
      groups.set(granteeKey, [...(groups.get(granteeKey) || []), permission]);
    });

    return Array.from(groups.entries()).map(([granteeKey, groupPermissions]) => {
      const sortedPermissions = [...groupPermissions].sort((left, right) =>
        `${left.companyName || ''}`.localeCompare(`${right.companyName || ''}`)
        || `${left.role || ''}`.localeCompare(`${right.role || ''}`)
      );
      const firstPermission = sortedPermissions[0];
      const latestCreatedAt = sortedPermissions
        .map(permission => permission.createdAt)
        .filter(createdAt => !!createdAt)
        .sort()
        .reverse()[0] || '';
      const companyNames = this.joinUniqueValues(sortedPermissions.map(permission => permission.companyName));
      const roles = this.joinUniqueValues(sortedPermissions.map(permission => this.getRoleLabel(permission.role)));
      const grantorNames = this.joinUniqueValues(sortedPermissions.map(permission => this.getGrantorName(permission) || this.getGrantorEmail(permission)));

      return {
        granteeKey,
        granteeUserId: firstPermission?.userId || '',
        granteeName: firstPermission?.name || '',
        granteeEmail: firstPermission?.email || '',
        permissions: sortedPermissions,
        permissionCount: sortedPermissions.length,
        companyNames,
        roles,
        grantorNames,
        latestCreatedAt,
        filterText: this.normalizeSearchText([
          firstPermission?.name,
          firstPermission?.email,
          companyNames,
          roles,
          grantorNames,
        ].join(' ')),
      };
    }).sort((left, right) => left.granteeName.localeCompare(right.granteeName));
  }

  private getGranteeKey(permission: TeamCompanyUser): string {
    return this.normalizeSearchText(permission.userId || permission.email || permission.name || permission.id);
  }

  private getPermissionFilterText(permission: TeamCompanyUser): string {
    return this.normalizeSearchText([
      permission.companyName,
      permission.name,
      permission.email,
      this.getGrantorName(permission),
      this.getGrantorEmail(permission),
      permission.role,
      this.getRoleLabel(permission.role),
    ].join(' '));
  }

  private joinUniqueValues(values: string[]): string {
    return Array.from(new Set(values.map(value => `${value || ''}`.trim()).filter(value => !!value))).join(', ');
  }

  private normalizeSearchText(value: string): string {
    return `${value || ''}`.trim().toLowerCase();
  }

}
